// src/components/createpost/CreatePostBoostDrawer.js

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import '../popups/StakeDrawer.css';

import closeIcon from '../../assets/close-icon.png';

const MIN_PI = 1;

const TIERS = [
  {
    hours: 4,
    label: '4h',
    feePercent: 4,
  },
  {
    hours: 12,
    label: '12h',
    feePercent: 8,
  },
  {
    hours: 24,
    label: '24h',
    feePercent: 16,
  },
  {
    hours: 36,
    label: '36h',
    feePercent: 27,
  },
  {
    hours: 48,
    label: '48h',
    feePercent: 40,
  },
];

function CreatePostBoostDrawer({
  open,
  selectedBoost = null,
  onSelectBoost,
  onClearBoost,
  onClose,
}) {
  const [amountPi, setAmountPi] = useState('');
  const [tierIndex, setTierIndex] = useState(0);
  const [amountErr, setAmountErr] = useState('');

  useEffect(() => {
    if (!open) return;

    if (selectedBoost) {
      setAmountPi(String(selectedBoost.amountPi || ''));

      const selectedTierIndex = TIERS.findIndex(
        (tier) =>
          Number(tier.hours) ===
          Number(selectedBoost.durationHours)
      );

      setTierIndex(
        selectedTierIndex >= 0
          ? selectedTierIndex
          : 0
      );
    } else {
      setAmountPi('');
      setTierIndex(0);
    }

    setAmountErr('');
  }, [open, selectedBoost]);

  const selectedTier =
    TIERS[
      Math.max(
        0,
        Math.min(TIERS.length - 1, tierIndex)
      )
    ];

  const numericAmount = useMemo(
    () => Number(amountPi),
    [amountPi]
  );

  const amountInvalid =
    !amountPi ||
    !Number.isFinite(numericAmount) ||
    numericAmount <= 0;

  const amountTooLow =
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    numericAmount < MIN_PI;

  useEffect(() => {
    if (amountInvalid) {
      setAmountErr('');
      return;
    }

    if (amountTooLow) {
      setAmountErr(`Minimum ${MIN_PI} Pi`);
      return;
    }

    setAmountErr('');
  }, [amountInvalid, amountTooLow]);

  const feePi = useMemo(() => {
    if (!Number.isFinite(numericAmount)) {
      return 0;
    }

    return +(
      numericAmount *
      (selectedTier.feePercent / 100)
    ).toFixed(4);
  }, [
    numericAmount,
    selectedTier.feePercent,
  ]);

  const refundablePi = useMemo(() => {
    if (!Number.isFinite(numericAmount)) {
      return 0;
    }

    return +(numericAmount - feePi).toFixed(4);
  }, [numericAmount, feePi]);

  const applyDisabled =
    amountInvalid || amountTooLow;

  const unlocksAtString = (
    hours = selectedTier.hours
  ) => {
    const unlockDate = new Date(
      Date.now() + hours * 60 * 60 * 1000
    );

    const pad = (value) =>
      String(value).padStart(2, '0');

    return `${unlockDate.getFullYear()}-${pad(
      unlockDate.getMonth() + 1
    )}-${pad(unlockDate.getDate())} ${pad(
      unlockDate.getHours()
    )}:${pad(unlockDate.getMinutes())}`;
  };

  const handleApplyBoost = () => {
    if (applyDisabled) return;

    onSelectBoost?.({
      amountPi: numericAmount,
      durationHours: selectedTier.hours,
    });

    onClose?.();
  };

  const handleRemoveBoost = () => {
    onClearBoost?.();
    onClose?.();
  };

  return (
    <div
      className={`stake-drawer ${
        open ? 'open' : ''
      }`}
      aria-hidden={!open}
    >
      <div
        className="stake-drawer__backdrop"
        onClick={onClose}
      />

      <div
        className="stake-drawer__panel"
        onClick={(event) =>
          event.stopPropagation()
        }
        role="dialog"
        aria-modal="true"
        aria-label="Stake to Boost"
      >
        <div className="stake-drawer__header">
          <div className="stake-drawer__title">
            Stake to Boost
          </div>

          <button
            type="button"
            className="stake-drawer__close"
            onClick={onClose}
            aria-label="Close"
          >
            <img
              src={closeIcon}
              alt="Close"
            />
          </button>
        </div>

        <div className="stake-drawer__content">
          <div className="stake-drawer__row">
            <label htmlFor="createpost-boost-amount">
              Amount (Pi)
            </label>

            <input
              id="createpost-boost-amount"
              type="number"
              min={MIN_PI}
              step="0.1"
              value={amountPi}
              onChange={(event) =>
                setAmountPi(event.target.value)
              }
              placeholder={`amount (Pi) · min ${MIN_PI}`}
              inputMode="decimal"
            />

            {amountErr && (
              <div className="stake-drawer__inline-error">
                {amountErr}
              </div>
            )}
          </div>

          <div className="stake-drawer__row">
            <label htmlFor="createpost-boost-duration">
              Duration
            </label>

            <div className="stake-drawer__range-wrap">
              <input
                id="createpost-boost-duration"
                type="range"
                min="0"
                max={TIERS.length - 1}
                step="1"
                value={tierIndex}
                onChange={(event) =>
                  setTierIndex(
                    Number(event.target.value)
                  )
                }
                className="stake-drawer__range"
              />

              <div className="stake-drawer__ticks">
                {TIERS.map((tier, index) => (
                  <div
                    key={tier.hours}
                    className={`tick ${
                      index === tierIndex
                        ? 'active'
                        : ''
                    }`}
                  >
                    <span>{tier.label}</span>

                    <em className="fee">
                      {tier.feePercent}%
                    </em>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="stake-drawer__preview">
            <div className="preview__row">
              <span className="k">
                Selected
              </span>

              <span className="v">
                {selectedTier.label}{' '}
                ({selectedTier.feePercent}% fee)
              </span>
            </div>

            <div className="preview__row">
              <span className="k">
                Stake
              </span>

              <span className="v">
                {Number.isFinite(numericAmount) &&
                numericAmount > 0
                  ? numericAmount.toFixed(2)
                  : '--'}{' '}
                Pi
              </span>
            </div>

            <div className="preview__row">
              <span className="k">
                Fee (now)
              </span>

              <span className="v">
                {Number.isFinite(numericAmount) &&
                numericAmount > 0
                  ? feePi.toFixed(4)
                  : '--'}{' '}
                Pi
              </span>
            </div>

            <div className="preview__row">
              <span className="k">
                Refundable
              </span>

              <span className="v">
                {Number.isFinite(numericAmount) &&
                numericAmount > 0
                  ? refundablePi.toFixed(4)
                  : '--'}{' '}
                Pi
              </span>
            </div>

            <div className="preview__row">
              <span className="k">
                Unlocks
              </span>

              <span className="v">
                {unlocksAtString()}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="stake-drawer__submit"
            disabled={applyDisabled}
            onClick={handleApplyBoost}
          >
            {Number.isFinite(numericAmount) &&
            numericAmount >= MIN_PI
              ? `Apply ${numericAmount.toFixed(
                  2
                )} Pi boost`
              : 'Apply boost'}
          </button>

          {selectedBoost && (
            <button
              type="button"
              className="createpost-boost-remove"
              onClick={handleRemoveBoost}
            >
              Remove boost
            </button>
          )}

          <div className="stake-drawer__hint">
            The boost will be charged and applied
            when your post is successfully
            published.
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatePostBoostDrawer;
